import {
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { PromptFields } from '@/components/ui/prompt-library-clean';

import {
  applyValues,
  contextSlots,
  FillContext,
  fillNames,
  isSubjectName,
  listFor,
  startValues,
  templateBodyOf,
  variablesByName,
} from './promptFill';
import { PromptMenuHeader } from './PromptMenuHeader';
import { PromptTemplateChip } from './SlashExtension.types';
import { usePromptFillContext } from './usePromptSubject';

/**
 * A template's fill step inside the prompt menu (VH3_CONNECT #592).
 * `insert`: picked from the `/` or `+` menu; the chip lands once every field
 * is filled, and Escape goes back to the list.
 * `reopen`: a chip in the composer; its values or wording change for this
 * message only, and Escape discards.
 */
export type PromptFill = {
  /** A new key starts the step again from `chip`. */
  key: string;
  mode: 'insert' | 'reopen';
  chip: PromptTemplateChip;
  /** The chip with its values, the text sent, and `edited`. */
  onSubmit: (chip: PromptTemplateChip) => void;
  /** Escape or "← back". */
  onBack: () => void;
};

type FillState = {
  key: string;
  values: Record<string, string>;
  /** The plain text field's text while "Edit wording" is open, else null. */
  wording: string | null;
};

const startState = (fill: PromptFill, context: FillContext): FillState => ({
  key: fill.key,
  values: startValues(fill.chip, context),
  wording: fill.chip.edited ? fill.chip.body : null,
});

export type PromptFillStepParts = {
  header: ReactNode;
  panel: ReactNode;
  footer: ReactNode;
  /** Escape (and ⌘↵ in the wording field), for the menu's wrapper. */
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
};

/** The fill step's header, panel and footer for Command Menu, or null. */
export function usePromptFillStep(
  fill: PromptFill | null | undefined
): PromptFillStepParts | null {
  // What the chat knows (#632): Context Variables are fields like the rest,
  // pre-filled with what it found.
  const context = usePromptFillContext();
  const [state, setState] = useState<FillState | null>(() =>
    fill ? startState(fill, context) : null
  );
  // A new template (or none) starts again; React re-renders before painting.
  if ((fill?.key ?? null) !== (state?.key ?? null)) {
    setState(fill ? startState(fill, context) : null);
  }

  // The lookup may answer after the step opened: empty Context Variable
  // fields take what it found. Fields with text stay as they are.
  const foundKey = JSON.stringify([context.names, context.values]);
  const chipBody = fill ? templateBodyOf(fill.chip) : null;
  useEffect(() => {
    if (chipBody == null) return;
    const list = listFor(chipBody, context);
    const listed = fillNames(chipBody, list).filter(name =>
      list.some(item => item.name === name)
    );
    setState(prev => {
      if (!prev) return prev;
      const next = { ...prev.values };
      let changed = false;
      for (const name of listed) {
        const found = context.values[name];
        if (found?.trim() && !prev.values[name]?.trim()) {
          next[name] = found;
          changed = true;
        }
      }
      return changed ? { ...prev, values: next } : prev;
    });
    // `foundKey` stands for the list and the values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foundKey, chipBody]);

  const panelRef = useRef<HTMLDivElement>(null);
  const current = fill && state?.key === fill.key ? state : null;
  const wordingOpen = current?.wording != null;
  // The field with focus, named under the sentence with its description.
  const [activeName, setActiveName] = useState<string | null>(null);

  // Focus lands on the first empty field (the first field when all are
  // filled), or in the wording field.
  useEffect(() => {
    if (!current) return;
    const panel = panelRef.current;
    if (!panel) return;
    if (wordingOpen) {
      const area = panel.querySelector<HTMLTextAreaElement>('textarea');
      area?.focus();
      area?.setSelectionRange(area.value.length, area.value.length);
      return;
    }
    const inputs = [
      ...panel.querySelectorAll<HTMLInputElement>('.ui-plic__input'),
    ];
    (inputs.find(input => !input.value.trim()) ?? inputs[0])?.focus();
    // Only when the step opens or swaps between fields and wording.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.key, wordingOpen]);

  const submit = useCallback(() => {
    if (!fill || !current) return;
    const { chip } = fill;
    const templateBody = templateBodyOf(chip);
    const list = listFor(templateBody, context);
    const names = fillNames(templateBody, list);
    const values = Object.fromEntries(
      names.map(name => [name, current.values[name] ?? ''])
    );
    if (names.some(name => !values[name].trim())) return;
    const filled = applyValues(templateBody, values, list);

    if (current.wording != null) {
      if (!current.wording.trim()) return;
      fill.onSubmit({
        ...chip,
        values,
        body: current.wording,
        // Saving the same words as the filled template is not an edit.
        edited: current.wording !== filled,
      });
      return;
    }
    fill.onSubmit({ ...chip, values, body: filled, edited: false });
  }, [context, current, fill]);

  if (!fill || !current) return null;

  const { chip, mode } = fill;
  const templateBody = templateBodyOf(chip);
  const list = listFor(templateBody, context);
  const names = fillNames(templateBody, list);
  const listItems = new Map(list.map(item => [item.name, item]));
  // A Context Variable is described by VH3's hint and labelled from the list;
  // the marker says where a found value came from, while the field still
  // holds it (quiet text: no colour, no pill).
  const described = {
    ...Object.fromEntries(
      names
        .filter(name => listItems.has(name))
        .map(name => [name, { description: listItems.get(name)!.hint }])
    ),
    ...variablesByName(chip.variables),
  };
  const labelOf = (name: string) => listItems.get(name)?.label ?? name;
  const slots = Object.fromEntries(
    Object.entries(contextSlots(list)).map(([name, slot]) => {
      const found = context.values[name];
      const fromSubject =
        !!context.marker &&
        isSubjectName(listItems.get(name)) &&
        !!found &&
        current.values[name] === found;
      return [name, fromSubject ? { ...slot, note: context.marker } : slot];
    })
  );
  const filledCount = names.filter(name => current.values[name]?.trim()).length;
  const missing = names.filter(name => !current.values[name]?.trim());
  const ready = missing.length === 0;
  const canSubmit =
    ready && (current.wording == null || !!current.wording.trim());
  const submitLabel = mode === 'insert' ? 'Insert' : 'Save';

  const setValue = (name: string, value: string) =>
    setState(prev =>
      prev ? { ...prev, values: { ...prev.values, [name]: value } } : prev
    );

  // Enter moves to the next field that is still empty, or finishes.
  const handleEnter = (name: string) => {
    const inputs = [
      ...(panelRef.current?.querySelectorAll<HTMLInputElement>(
        '.ui-plic__input'
      ) ?? []),
    ];
    const from = inputs.findIndex(input => input.dataset.var === name);
    const next = [...inputs.slice(from + 1), ...inputs.slice(0, from)].find(
      input => !input.value.trim()
    );
    if (next) next.focus();
    else if (ready) submit();
  };

  const openWording = () =>
    setState(prev =>
      prev
        ? {
            ...prev,
            wording: applyValues(templateBody, prev.values, list),
          }
        : prev
    );

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      // The menu's host (a Chakra popover, the `/` suggestion) never sees it.
      event.preventDefault();
      event.stopPropagation();
      fill.onBack();
    } else if (
      event.key === 'Enter' &&
      (event.metaKey || event.ctrlKey) &&
      current.wording != null
    ) {
      event.preventDefault();
      submit();
    }
  };

  const header = (
    <PromptMenuHeader
      label={chip.title}
      onBack={mode === 'insert' ? fill.onBack : undefined}
    />
  );

  const activeDescription = activeName
    ? described[activeName]?.description
    : undefined;
  const verb = submitLabel.toLowerCase();

  const panel = (
    <div
      ref={panelRef}
      className='prompt-fill'
      data-cy='prompt-fill'
      onFocusCapture={event => {
        const name = (event.target as HTMLElement).dataset?.var;
        if (name) setActiveName(name);
      }}
      onBlurCapture={event => {
        const name = (event.target as HTMLElement).dataset?.var;
        if (name && name === activeName) setActiveName(null);
      }}
    >
      {current.wording == null ? (
        <>
          <PromptFields
            className='prompt-fill__fields'
            body={templateBody}
            values={current.values}
            variables={described}
            contexts={slots}
            onChange={setValue}
            onEnter={handleEnter}
          />
          {names.length > 0 && (
            // Which field has focus and what goes in it; how many are
            // filled; and, until all are, which one Insert is waiting for.
            <div className='prompt-fill__status' data-cy='prompt-fill-status'>
              <span className='prompt-fill__field'>
                {activeName ? (
                  <>
                    <b>{labelOf(activeName)}</b>
                    {activeDescription && ` · ${activeDescription}`}
                  </>
                ) : ready ? (
                  `Ready to ${verb}`
                ) : (
                  `Fill ${labelOf(missing[0])} to ${verb}`
                )}
              </span>
              <span className='prompt-fill__progress'>
                <span className='prompt-fill__bars' aria-hidden='true'>
                  {names.map(name => (
                    <span
                      key={name}
                      className='prompt-fill__bar'
                      data-on={current.values[name]?.trim() ? '' : undefined}
                    />
                  ))}
                </span>
                {filledCount} of {names.length} filled
              </span>
            </div>
          )}
        </>
      ) : (
        <textarea
          className='prompt-fill__wording'
          data-cy='prompt-fill-wording'
          aria-label='Wording for this message'
          rows={4}
          value={current.wording}
          onChange={event => {
            const wording = event.target.value;
            setState(prev => (prev ? { ...prev, wording } : prev));
          }}
        />
      )}
    </div>
  );

  // The links, then one hint per key: the card's footer spaces the pairs
  // apart. On a touch screen the key hints give way to a Discard / Back
  // link, since there is no keyboard to press them on.
  const footer = (
    <>
      <span className='prompt-fill__hints' data-cy='prompt-fill-hints'>
        {mode === 'reopen' && current.wording == null && (
          <button
            type='button'
            className='prompt-fill__link'
            data-cy='prompt-fill-edit-wording'
            onClick={openWording}
          >
            Edit wording
          </button>
        )}
        <button
          type='button'
          className='prompt-fill__link prompt-fill__link--touch'
          data-cy='prompt-fill-cancel'
          onClick={fill.onBack}
        >
          {mode === 'insert' ? 'Back' : 'Discard'}
        </button>
        <span className='prompt-menu__hint prompt-fill__key'>
          {current.wording == null ? (
            <>
              <kbd>↵</kbd>
              {ready ? submitLabel.toLowerCase() : 'next field'}
            </>
          ) : (
            <>
              <span className='ui-cmdc__keys'>
                <kbd>⌘</kbd>
                <kbd>↵</kbd>
              </span>
              {submitLabel.toLowerCase()}
            </>
          )}
        </span>
        {mode === 'insert' && (
          <span className='prompt-menu__hint prompt-fill__key'>
            <kbd>tab</kbd>
            move
          </span>
        )}
        <span className='prompt-menu__hint prompt-fill__key'>
          <kbd>esc</kbd>
          {mode === 'insert' ? 'back' : 'discard'}
        </span>
      </span>
      <button
        type='button'
        className='prompt-fill__submit'
        data-cy='prompt-fill-submit'
        title={ready ? undefined : `Fill ${labelOf(missing[0])} to ${verb}`}
        disabled={!canSubmit}
        onClick={submit}
      >
        {submitLabel}
      </button>
    </>
  );

  return { header, panel, footer, onKeyDown };
}
